/**
 * Pure helpers for the sim-wide Dashboard Item range editor (builder tab).
 * Integer *Val fields, defaults, and API payload shaping. No React.
 */

/** Backend stores *Val as BIGINT (Java Long); at most 15 decimal digits of magnitude. */
export const NUMERIC_RANGE_VALUE_FIELDS = [
  "redMinVal",
  "redMaxVal",
  "yellowMinVal",
  "yellowMaxVal",
  "greenMinVal",
  "greenMaxVal",
];

export const MAX_RANGE_VALUE_INPUT_LENGTH = 15;

/** Matches backend: fifteen 9s — largest magnitude allowed (whole numbers only). */
export const MAX_RANGE_ABS_INTEGER = 999_999_999_999_999;

export const initialDashboardRangeState = {
  simId: null,
  redMinOp: ">=",
  redMinVal: "",
  redMaxOp: "<=",
  redMaxVal: "",
  yellowMinOp: ">=",
  yellowMinVal: "",
  yellowMaxOp: "<=",
  yellowMaxVal: "",
  greenMinOp: ">=",
  greenMinVal: "",
  greenMaxOp: "<=",
  greenMaxVal: "",
};

/** Integer entry: digits and optional leading "-". Max length capped. */
export const sanitizeIntegerRangeValue = (raw) => {
  const maxLen = MAX_RANGE_VALUE_INPUT_LENGTH;
  let s = "";
  for (const ch of String(raw ?? "")) {
    if (s.length >= maxLen) break;
    if (ch >= "0" && ch <= "9") {
      s += ch;
    } else if (ch === "-" && s.length === 0) {
      s += ch;
    }
  }
  if (s === "-") return s;
  if (s.startsWith("-")) {
    return "-" + s.slice(1).split("-").join("");
  }
  return s.split("-").join("");
};

/** Normalise API value (number or string) to controlled form string. */
export const rangeValueForForm = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) {
    return sanitizeIntegerRangeValue(String(Math.trunc(v)));
  }
  return sanitizeIntegerRangeValue(String(v));
};

/** Coerce form string to integer for JSON body, or null if empty / invalid / out of policy. */
export const integerOrNullForApi = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === "" || s === "-") return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isSafeInteger(n)) return null;
  if (Math.abs(n) > MAX_RANGE_ABS_INTEGER) return null;
  return n;
};

/** Apply `rangeValueForForm` to every *Val field on a DTO clone. */
export const withFormRangeValues = (dto) => {
  const out = { ...dto };
  NUMERIC_RANGE_VALUE_FIELDS.forEach((key) => {
    out[key] = rangeValueForForm(dto[key]);
  });
  return out;
};

/** Build POST body: pass-through operators + integer/null *Val fields. */
export const buildDashboardRangeApiPayload = (formState, simId) => {
  const payload = { ...formState, simId };
  NUMERIC_RANGE_VALUE_FIELDS.forEach((key) => {
    payload[key] = integerOrNullForApi(formState[key]);
  });
  return payload;
};
